import React, { useState } from "react";
import { Eye, EyeOff, KeyRound, ArrowLeft, Check, MonitorDown } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";
import { BACKEND_HOST } from "../config";

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.response ? fallback : "Server non raggiungibile, riprova";
}

// Host del server: mostrato nel login così si capisce subito su quale ambiente (web/PWA) si sta entrando
const SERVER_HOST = BACKEND_HOST;

function PasswordInput({ value, onChange, placeholder, testid, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="authx-pw">
      <input
        className="authx-input"
        name={autoComplete || "password"}
        autoComplete={autoComplete || "current-password"}
        type={show ? "text" : "password"}
        placeholder={placeholder || "••••••••"}
        value={value}
        onChange={onChange}
        data-testid={testid}
      />
      <button
        className="authx-eye"
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? "Nascondi password" : "Mostra password"}
      >
        {show ? <EyeOff size={18} strokeWidth={2.2} /> : <Eye size={18} strokeWidth={2.2} />}
      </button>
    </div>
  );
}

// Pagina di accesso POSTA AI - autenticazione reale (utenti salvati sul server)
export default function AuthPage({ onLogin }) {
  const [busy, setBusy] = useState(false);
  const [loginErr, setLoginErr] = useState("");
  const saved = api.loadLoginData();
  const [ricorda, setRicorda] = useState(true);
  const [view, setView] = useState("login"); // login | register | recover
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState(saved?.email || "");
  const [password, setPassword] = useState(saved?.password || "");
  const [conferma, setConferma] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [recCode, setRecCode] = useState(null);
  const [recInput, setRecInput] = useState("");
  const [recPassword, setRecPassword] = useState("");

  const reset = () => {
    setLoginErr("");
    setPassword("");
    setConferma("");
    setRecCode(null);
    setRecInput("");
    setRecPassword("");
  };

  const accedi = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    setBusy(true);
    try {
      const u = await api.login(email, password);
      if (ricorda) api.saveLoginData(email.trim(), password);
      else api.clearLoginData();
      toast.success(`Bentornato, ${u.nome}!`);
      onLogin(u);
    } catch (err) {
      const msg = errMsg(err, "Accesso non riuscito");
      setLoginErr(msg);
      toast.error(msg, {
        description: err?.response?.status === 401 ? `Le credenziali valgono solo sull'indirizzo dove ti sei registrato (questo: ${SERVER_HOST}).` : undefined,
        duration: 7000,
      });
    } finally {
      setBusy(false);
    }
  };

  const registra = async (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    const em = email.trim().toLowerCase();
    if (!em.includes("@") || em.length < 5) {
      toast.error("Inserisci un'email valida");
      return;
    }
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== conferma) {
      toast.error("Le password non coincidono");
      return;
    }
    setBusy(true);
    try {
      const u = await api.register(nome, em, password);
      api.saveLoginData(em, password);
      toast.success("Registrazione completata!");
      onLogin(u);
    } catch (err) {
      toast.error(errMsg(err, "Registrazione non riuscita"));
    } finally {
      setBusy(false);
    }
  };

  const inviaCodice = async () => {
    setBusy(true);
    try {
      const r = await api.forgotPassword(recEmail);
      setRecCode(r.code);
      toast.success("Codice di verifica generato");
    } catch (err) {
      toast.error(errMsg(err, "Richiesta non riuscita"));
    } finally {
      setBusy(false);
    }
  };

  const confermaRecupero = async () => {
    if (recPassword.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(recEmail, recInput, recPassword);
      toast.success("Password aggiornata! Ora puoi accedere");
      reset();
      setView("login");
    } catch (err) {
      toast.error(errMsg(err, "Aggiornamento non riuscito"));
    } finally {
      setBusy(false);
    }
  };

  const googleDemo = () => {
    toast("Accesso con Google non ancora attivo: usa email e password");
  };

  const installApp = () => {
    toast("Installazione app: usa 'Aggiungi a schermata Home' del browser (demo)");
  };

  return (
    <div className="authx" data-testid="auth-page">
      {/* Meta' sinistra: immagine hero (solo desktop) */}
      <div className="authx-hero">
        <img src="/login-hero-blue.jpg" alt="" className="authx-hero-img" />
        <div className="authx-hero-overlay" />
        <div className="authx-hero-text">
          <p className="authx-kicker">Webmail intelligente</p>
          <h2 className="authx-hero-title">
            Tutte le tue caselle.
            <br />
            Un solo posto.
            <br />
            <span>Con l'AI che scrive per te.</span>
          </h2>
        </div>
      </div>

      {/* Meta' destra: form */}
      <div className="authx-right">
        <div className="authx-box">
          <p className="authx-kicker">Benvenuto</p>
          <div className="authx-title-row">
            <h1 className="authx-title">
              POSTA <span>AI</span>
            </h1>
            <span className="authx-mail-badge">
              <img src="/posta-ai-icon.png" alt="POSTA AI" className="authx-logo-img" />
            </span>
          </div>
          <p className="authx-sub">La tua posta, con l'intelligenza artificiale.</p>

          {view === "login" && (
            <>
              <form className="authx-form" onSubmit={accedi}>
                <div>
                  <label className="authx-label">Email</label>
                  <input
                    className="authx-input"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="nome@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <label className="authx-label">Password</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    testid="login-password"
                  />
                </div>
                <label className="authx-remember" data-testid="login-remember">
                  <input type="checkbox" checked={ricorda} onChange={(e) => setRicorda(e.target.checked)} />
                  Ricorda i dati di accesso su questo dispositivo
                </label>
                <button className="authx-btn" type="submit" disabled={busy} data-testid="login-submit">
                  {busy ? "Accesso..." : "Accedi"}
                </button>
              </form>
              {loginErr && (
                <div className="authx-error" data-testid="login-error">
                  {loginErr}
                </div>
              )}

              <button className="authx-link" onClick={() => { reset(); setView("recover"); }} data-testid="goto-recover">
                Password dimenticata?
              </button>
              <button className="authx-link" onClick={() => { reset(); setView("register"); }} data-testid="goto-register">
                Non hai un account? <span className="authx-accent">Registrati</span>
              </button>
              <div className="authx-server" data-testid="auth-server-host">Server: {SERVER_HOST}</div>

              <div className="authx-actions">
                <button className="authx-btn-outline" onClick={googleDemo} data-testid="google-login">
                  <span className="authx-g">G</span> Accedi con Google
                </button>
                <button className="authx-btn-outline" onClick={installApp} data-testid="install-app">
                  <MonitorDown size={17} strokeWidth={2.2} className="authx-accent-icon" />
                  Installa l'app sul dispositivo
                </button>
              </div>
            </>
          )}

          {view === "register" && (
            <>
              <form className="authx-form" onSubmit={registra}>
                <div>
                  <label className="authx-label">Nome</label>
                  <input
                    className="authx-input"
                    placeholder="Il tuo nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    data-testid="register-name"
                  />
                </div>
                <div>
                  <label className="authx-label">Email</label>
                  <input
                    className="authx-input"
                    type="email"
                    placeholder="nome@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <label className="authx-label">Password (min. 6 caratteri)</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    testid="register-password"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="authx-label">Conferma password</label>
                  <PasswordInput
                    value={conferma}
                    onChange={(e) => setConferma(e.target.value)}
                    testid="register-confirm"
                    autoComplete="new-password"
                  />
                </div>
                <button className="authx-btn" type="submit" disabled={busy} data-testid="register-submit">
                  {busy ? "Creazione..." : "Crea account"}
                </button>
              </form>

              <button className="authx-link" onClick={() => { reset(); setView("login"); }} data-testid="goto-login">
                Hai gia' un account? <span className="authx-accent">Accedi</span>
              </button>

              <div className="authx-actions">
                <button className="authx-btn-outline" onClick={googleDemo} data-testid="google-register">
                  <span className="authx-g">G</span> Registrati con Google
                </button>
              </div>
            </>
          )}

          {view === "recover" && (
            <>
              <button className="authx-link authx-back" onClick={() => { reset(); setView("login"); }} data-testid="recover-back">
                <ArrowLeft size={16} strokeWidth={2.4} /> Torna all'accesso
              </button>
              <h2 className="authx-h2">
                <KeyRound size={19} strokeWidth={2.2} className="authx-accent-icon" /> Recupero password
              </h2>

              {!recCode ? (
                <div className="authx-form">
                  <div>
                    <label className="authx-label">Email del tuo account</label>
                    <input
                      className="authx-input"
                      type="email"
                      placeholder="nome@esempio.it"
                      value={recEmail}
                      onChange={(e) => setRecEmail(e.target.value)}
                      data-testid="recover-email"
                    />
                  </div>
                  <button className="authx-btn" onClick={inviaCodice} disabled={busy} data-testid="recover-send">
                    Invia codice di verifica
                  </button>
                </div>
              ) : (
                <div className="authx-form">
                  <div className="authx-code" data-testid="recover-code-banner">
                    Codice di verifica: <strong>{recCode}</strong>
                    <div className="authx-code-note">Valido 15 minuti</div>
                  </div>
                  <div>
                    <label className="authx-label">Codice di verifica</label>
                    <input
                      className="authx-input"
                      placeholder="6 cifre"
                      value={recInput}
                      onChange={(e) => setRecInput(e.target.value)}
                      data-testid="recover-code"
                    />
                  </div>
                  <div>
                    <label className="authx-label">Nuova password (min. 6 caratteri)</label>
                    <PasswordInput
                      value={recPassword}
                      onChange={(e) => setRecPassword(e.target.value)}
                      placeholder="Nuova password"
                      testid="recover-password"
                    />
                  </div>
                  <button className="authx-btn" onClick={confermaRecupero} disabled={busy} data-testid="recover-submit">
                    <Check size={17} strokeWidth={2.6} /> Aggiorna password
                  </button>
                </div>
              )}
            </>
          )}

          <p className="authx-note">L'accesso viene ricordato su questo dispositivo per 30 giorni.</p>
        </div>
      </div>
    </div>
  );
}
